import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchFilters } from './types';
import './FilterPanel.scss';

interface FilterPanelProps {
  filters: SearchFilters;
  onFiltersChange: (filters: Partial<SearchFilters>) => void;
  showFilters: boolean;
  onClose: () => void;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onFiltersChange,
  showFilters,
  onClose
}) => {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = useState<SearchFilters>(filters);

  // Update local filters when props change
  React.useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleListingTypeChange = useCallback((type: 'sell' | 'buy' | 'both') => {
    setLocalFilters(prev => ({ ...prev, listingType: type }));
  }, []);

  const handleSizeChange = useCallback((field: 'min' | 'max', value: string) => {
    const numValue = value === '' ? null : parseInt(value);
    setLocalFilters(prev => ({
      ...prev,
      size: { ...prev.size, [field]: numValue }
    }));
  }, []);

  const handleRoomsChange = useCallback((rooms: string | null) => {
    setLocalFilters(prev => ({ ...prev, rooms }));
  }, []);

  const handleFloorChange = useCallback((field: 'min' | 'max', value: string) => {
    const numValue = value === '' ? null : parseInt(value);
    setLocalFilters(prev => ({
      ...prev,
      floor: { ...prev.floor, [field]: numValue }
    }));
  }, []);

  const handleRenovationStatusChange = useCallback((status: string | null) => {
    setLocalFilters(prev => ({ ...prev, renovationStatus: status }));
  }, []);

  const handleAmenityChange = useCallback((amenity: 'hasElevator' | 'hasBalcony', value: boolean) => {
    setLocalFilters(prev => ({ ...prev, [amenity]: value }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    onFiltersChange(localFilters);
    onClose();
  }, [localFilters, onFiltersChange, onClose]);

  const handleClearAll = useCallback(() => {
    const clearedFilters: SearchFilters = {
      listingType: 'both',
      location: localFilters.location, // Keep location
      priceRange: { min: null, max: null },
      size: { min: null, max: null },
      rooms: null,
      floor: { min: null, max: null },
      renovationStatus: null,
      hasElevator: null,
      hasBalcony: null
    };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localFilters.location]);

  const getActiveFiltersCount = useCallback(() => {
    let count = 0;
    if (localFilters.listingType !== 'both') count++;
    if (localFilters.size.min || localFilters.size.max) count++;
    if (localFilters.rooms) count++;
    if (localFilters.floor.min || localFilters.floor.max) count++;
    if (localFilters.renovationStatus) count++;
    if (localFilters.hasElevator !== null) count++;
    if (localFilters.hasBalcony !== null) count++;
    return count;
  }, [localFilters]);

  if (!showFilters) return null;

  return (
    <div className="filter-panel-overlay">
      <div className="filter-panel">
        <div className="filter-panel__header">
          <button className="filter-panel__close-button" onClick={onClose}>
            <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
              <path d="m6 6 20 20M26 6 6 26"></path>
            </svg>
          </button>
          <h2 className="filter-panel__title">{t('exploreListings.filters')}</h2>
          {getActiveFiltersCount() > 0 && (
            <button className="filter-panel__clear-button" onClick={handleClearAll}>
              {t('exploreListings.clearAll')}
            </button>
          )}
        </div>

        <div className="filter-panel__content">
          {/* Listing Type */}
          <div className="filter-panel__section">
            <h3 className="filter-panel__section-title">{t('exploreListings.listingType')}</h3>
            <div className="filter-panel__button-group filter-panel__button-group--listing-type">
              <button
                className={`filter-panel__toggle-button filter-panel__toggle-button--listing-type ${localFilters.listingType === 'both' ? 'filter-panel__toggle-button--active' : ''}`}
                onClick={() => handleListingTypeChange('both')}
              >
                {t('exploreListings.both')}
              </button>
              <button
                className={`filter-panel__toggle-button filter-panel__toggle-button--listing-type ${localFilters.listingType === 'sell' ? 'filter-panel__toggle-button--active' : ''}`}
                onClick={() => handleListingTypeChange('sell')}
              >
                {t('exploreListings.forSale')}
              </button>
              <button
                className={`filter-panel__toggle-button filter-panel__toggle-button--listing-type ${localFilters.listingType === 'buy' ? 'filter-panel__toggle-button--active' : ''}`}
                onClick={() => handleListingTypeChange('buy')}
              >
                {t('exploreListings.buyingInterests')}
              </button>
            </div>
          </div>

          {/* Property Size */}
          <div className="filter-panel__section">
            <h3 className="filter-panel__section-title">{t('exploreListings.propertySize')}</h3>
            <div className="filter-panel__range-inputs">
              <div className="filter-panel__input-wrapper">
                <input
                  type="number"
                  className="filter-panel__input"
                  placeholder={t('exploreListings.minSize')}
                  value={localFilters.size.min || ''}
                  onChange={(e) => handleSizeChange('min', e.target.value)}
                />
                <span className="filter-panel__input-suffix">m²</span>
              </div>
              <span className="filter-panel__range-separator">–</span>
              <div className="filter-panel__input-wrapper">
                <input
                  type="number"
                  className="filter-panel__input"
                  placeholder={t('exploreListings.maxSize')}
                  value={localFilters.size.max || ''}
                  onChange={(e) => handleSizeChange('max', e.target.value)}
                />
                <span className="filter-panel__input-suffix">m²</span>
              </div>
            </div>
          </div>

          {/* Rooms */}
          <div className="filter-panel__section">
            <h3 className="filter-panel__section-title">{t('exploreListings.rooms')}</h3>
            <div className="filter-panel__button-group filter-panel__button-group--rooms">
              <button
                className={`filter-panel__toggle-button ${!localFilters.rooms ? 'filter-panel__toggle-button--active' : ''}`}
                onClick={() => handleRoomsChange(null)}
              >
                {t('exploreListings.any')}
              </button>
              {['1', '2', '3', '4', '5+'].map(room => (
                <button
                  key={room}
                  className={`filter-panel__toggle-button ${localFilters.rooms === room ? 'filter-panel__toggle-button--active' : ''}`}
                  onClick={() => handleRoomsChange(room)}
                >
                  {room}
                </button>
              ))}
            </div>
          </div>

          {/* Floor */}
          <div className="filter-panel__section">
            <h3 className="filter-panel__section-title">{t('exploreListings.floor')}</h3>
            <div className="filter-panel__range-inputs">
              <div className="filter-panel__input-wrapper">
                <input
                  type="number"
                  className="filter-panel__input"
                  placeholder={t('exploreListings.minFloor')}
                  value={localFilters.floor.min || ''}
                  onChange={(e) => handleFloorChange('min', e.target.value)}
                />
              </div>
              <span className="filter-panel__range-separator">–</span>
              <div className="filter-panel__input-wrapper">
                <input
                  type="number"
                  className="filter-panel__input"
                  placeholder={t('exploreListings.maxFloor')}
                  value={localFilters.floor.max || ''}
                  onChange={(e) => handleFloorChange('max', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Renovation Status */}
          <div className="filter-panel__section">
            <h3 className="filter-panel__section-title">{t('exploreListings.renovationStatus')}</h3>
            <select
              className="filter-panel__select"
              value={localFilters.renovationStatus || ''}
              onChange={(e) => handleRenovationStatusChange(e.target.value || null)}
            >
              <option value="">{t('exploreListings.anyCondition')}</option>
              <option value="recently_renovated">{t('exploreListings.recentlyRenovated')}</option>
              <option value="good_condition">{t('exploreListings.goodCondition')}</option>
              <option value="needs_renovation">{t('exploreListings.needsRenovation')}</option>
            </select>
          </div>

          {/* Amenities */}
          <div className="filter-panel__section">
            <h3 className="filter-panel__section-title">{t('exploreListings.amenities')}</h3>
            <div className="filter-panel__checkbox-group">
              <label className="filter-panel__checkbox-label">
                <input
                  type="checkbox"
                  className="filter-panel__checkbox"
                  checked={localFilters.hasElevator === true}
                  onChange={(e) => handleAmenityChange('hasElevator', e.target.checked ? true : null)}
                />
                <span>{t('exploreListings.hasElevator')}</span>
              </label>
              <label className="filter-panel__checkbox-label">
                <input
                  type="checkbox"
                  className="filter-panel__checkbox"
                  checked={localFilters.hasBalcony === true}
                  onChange={(e) => handleAmenityChange('hasBalcony', e.target.checked ? true : null)}
                />
                <span>{t('exploreListings.hasBalcony')}</span>
              </label>
            </div>
          </div>
        </div>

        <div className="filter-panel__footer">
          <button className="filter-panel__cancel-button" onClick={onClose}>
            {t('exploreListings.cancel')}
          </button>
          <button className="filter-panel__apply-button" onClick={handleApplyFilters}>
            {t('exploreListings.showResults')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel; 